/** @jsxImportSource @emotion/react */
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as authSelectors from "../../../auth/authSelectors";
import { useSelector } from "react-redux";
import { AdyenBusinessLineListItem } from "../../models/adyen-business-line";
import { Table } from "../../../../components/table-components/Table";
import { colors } from "../../../../components/colors";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableBody } from "../../../../components/table-components/TableBody";
import { TableRowHeader } from "../../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../../components/table-components/TableCellHeaderText";
import { TableCellLink } from "../../../../components/table-components/TableCellLink";
import { StyledText } from "../../../../assets/typography/typography";
import { TableEllipsisDropdown } from "../../../../components/TableEllipsisDropdown";
import { DropdownItemLink } from "../../../common/DropdownItemLink";
import { DropdownItemButton } from "../../../common/DropdownItemButton";
import { routes } from "../../../../appRoutingHelpers";
import { useParams } from "react-router";
import { useDeleteAdyenAccountHolderBusinessLine } from "../../service/adyen-account-holders-api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";

type Props = {
  businessLines: AdyenBusinessLineListItem[];
};

export const AdyenAccountHolderBusinessLinesTable = ({
  businessLines,
}: Props) => {
  const { id } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: deleteBusinessLine,
    isLoading: isDeletingBusinessLine,
  } = useDeleteAdyenAccountHolderBusinessLine(id);

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText>Industry code</TableCellHeaderText>
          <TableCellHeaderText>Industry code name</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {businessLines.map((ah, i) => {
          const href = routes.adyenAccountHolderBusinessLinesEdit(
            organizationKeyName,
            id,
            ah.id
          );

          return (
            <TableRow key={i}>
              <TableCellLink href={href} className="pl-5">
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {ah.industryCode || "-"}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {ah.industryCodeName || "-"}
                </StyledText>
              </TableCellLink>
              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemLink
                          icon={<FontAwesomeIcon icon={solid("pen")} />}
                          to={href}
                        >
                          Edit
                        </DropdownItemLink>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          icon={<FontAwesomeIcon icon={solid("trash")} />}
                          disabled={isDeletingBusinessLine}
                          onClick={async () => {
                            try {
                              await deleteBusinessLine({
                                data: { id: ah.id },
                              });
                              toastr.success(
                                "Success",
                                "Business line deleted"
                              );
                            } catch (error: unknown) {
                              const errorMessage = getErrorMessage(error);
                              toastr.error("Error", errorMessage);
                            }
                          }}
                        >
                          Delete
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
